import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { collection, addDoc, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { logger } from '../utils/logger'; // ✅ CORRECTION : chemin correct

/**
 * Service de gestion des notifications push avec Firebase Cloud Messaging
 */

class NotificationService {
  constructor() {
    this.messaging = null;
    this.vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
  }

  async initialize() {
    try {
      this.messaging = getMessaging();
      logger.log('✅ Firebase Messaging initialisé');
      return true;
    } catch (error) {
      logger.error('❌ Erreur initialisation Firebase Messaging:', error);
      return false;
    }
  }

  async requestPermission(userId) {
    try {
      if (!('Notification' in window)) {
        logger.warn('⚠️ Les notifications ne sont pas supportées par ce navigateur');
        return null;
      }

      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        logger.log('✅ Permission pour les notifications accordée');
        
        const token = await getToken(this.messaging, { vapidKey: this.vapidKey });
        
        if (token) {
          logger.log('✅ Token FCM obtenu:', token.substring(0, 20) + '...');
          await this.saveTokenToFirestore(userId, token);
          return token;
        } else {
          logger.warn('⚠️ Aucun token FCM disponible');
          return null;
        }
      } else {
        logger.warn('⚠️ Permission pour les notifications refusée');
        return null;
      }
    } catch (error) {
      logger.error('❌ Erreur lors de la demande de permission:', error);
      return null;
    }
  }

  async saveTokenToFirestore(userId, token) {
    try {
      const tokensRef = collection(db, 'fcmTokens');
      const q = query(tokensRef, where('userId', '==', userId), where('token', '==', token));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        await addDoc(tokensRef, {
          userId,
          token,
          createdAt: new Date(),
          lastUsed: new Date(),
          platform: this.getPlatform(),
          browser: this.getBrowser()
        });
        logger.log('✅ Token FCM enregistré dans Firestore');
      } else {
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, {
          lastUsed: new Date()
        });
        logger.log('✅ Token FCM mis à jour dans Firestore');
      }
    } catch (error) {
      logger.error('❌ Erreur lors de l\'enregistrement du token:', error);
    }
  }

  onMessageListener(callback) {
    if (!this.messaging) {
      logger.warn('⚠️ Messaging non initialisé');
      return;
    }

    onMessage(this.messaging, (payload) => {
      logger.log('📩 Message reçu:', payload);
      
      if (Notification.permission === 'granted') {
        const { title, body, icon } = payload.notification || {};
        new Notification(title || 'Notification', {
          body: body || '',
          icon: icon || '/logo192.png',
          badge: '/logo192.png'
        });
      }

      if (callback) {
        callback(payload);
      }
    });
  }

  async scheduleEventReminders(event, userIds = []) {
    try {
      const remindersRef = collection(db, 'scheduledReminders');
      
      const eventDate = new Date(event.date);
      
      const oneDayBefore = new Date(eventDate);
      oneDayBefore.setDate(oneDayBefore.getDate() - 1);
      oneDayBefore.setHours(20, 0, 0, 0);

      const oneHourBefore = new Date(eventDate);
      oneHourBefore.setHours(oneHourBefore.getHours() - 1);

      const reminders = [
        {
          eventId: event.id,
          eventTitle: event.title,
          eventDate: eventDate,
          eventType: event.type || 'event',
          scheduledFor: oneDayBefore,
          type: '1_day_before',
          message: `Rappel : ${event.title} demain à ${eventDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`,
          userIds: userIds.length > 0 ? userIds : ['all'],
          sent: false,
          cancelled: false,
          createdAt: new Date()
        },
        {
          eventId: event.id,
          eventTitle: event.title,
          eventDate: eventDate,
          eventType: event.type || 'event',
          scheduledFor: oneHourBefore,
          type: '1_hour_before',
          message: `Le ${event.title} commence dans 1 heure !`,
          userIds: userIds.length > 0 ? userIds : ['all'],
          sent: false,
          cancelled: false,
          createdAt: new Date()
        }
      ];

      for (const reminder of reminders) {
        if (reminder.scheduledFor > new Date()) {
          await addDoc(remindersRef, reminder);
          logger.log(`✅ Rappel planifié: ${reminder.type} pour ${event.title}`);
        }
      }

      return true;
    } catch (error) {
      logger.error('❌ Erreur lors de la planification des rappels:', error);
      return false;
    }
  }

  async cancelEventReminders(eventId) {
    try {
      const remindersRef = collection(db, 'scheduledReminders');
      const q = query(remindersRef, where('eventId', '==', eventId), where('sent', '==', false));
      const querySnapshot = await getDocs(q);

      const deletePromises = querySnapshot.docs.map(doc => 
        updateDoc(doc.ref, { cancelled: true })
      );

      await Promise.all(deletePromises);
      logger.log(`✅ Rappels annulés pour l'événement ${eventId}`);
      return true;
    } catch (error) {
      logger.error('❌ Erreur lors de l\'annulation des rappels:', error);
      return false;
    }
  }

  async getUserNotificationPreferences(userId) {
    try {
      const prefsRef = collection(db, 'notificationPreferences');
      const q = query(prefsRef, where('userId', '==', userId));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        return querySnapshot.docs[0].data();
      }

      return {
        userId,
        events: true,
        cultes: true,
        anniversaires: true,
        announcements: true,
        oneDayBefore: true,
        oneHourBefore: true
      };
    } catch (error) {
      logger.error('❌ Erreur lors de la récupération des préférences:', error);
      return null;
    }
  }

  async updateUserNotificationPreferences(userId, preferences) {
    try {
      const prefsRef = collection(db, 'notificationPreferences');
      const q = query(prefsRef, where('userId', '==', userId));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, {
          ...preferences,
          updatedAt: new Date()
        });
      } else {
        await addDoc(prefsRef, {
          userId,
          ...preferences,
          createdAt: new Date()
        });
      }

      logger.log('✅ Préférences de notification mises à jour');
      return true;
    } catch (error) {
      logger.error('❌ Erreur lors de la mise à jour des préférences:', error);
      return false;
    }
  }

  getPlatform() {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/android/.test(userAgent)) return 'Android';
    if (/iphone|ipad|ipod/.test(userAgent)) return 'iOS';
    if (/win/.test(userAgent)) return 'Windows';
    if (/mac/.test(userAgent)) return 'macOS';
    if (/linux/.test(userAgent)) return 'Linux';
    return 'Unknown';
  }

  getBrowser() {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/chrome/.test(userAgent) && !/edge/.test(userAgent)) return 'Chrome';
    if (/firefox/.test(userAgent)) return 'Firefox';
    if (/safari/.test(userAgent) && !/chrome/.test(userAgent)) return 'Safari';
    if (/edge/.test(userAgent)) return 'Edge';
    return 'Unknown';
  }
}

const notificationService = new NotificationService();

export default notificationService;